// src/pages/contract/ContractList.jsx
import React, { useEffect, useState, useCallback, useContext } from "react";
import { AuthContext } from "../../../context/AuthContext";
import { ENDPOINTS, API_BASE_DOCS } from "../../../config/api";
import "bootstrap/dist/css/bootstrap.min.css";
import { useNavigate } from "react-router-dom";

// ⭐ NUEVA PAGINACIÓN
import Pagination from "../../../components/private/pagination";

const ContractGH = () => {
  const { authFetch, token } = useContext(AuthContext);
  const navigate = useNavigate();

  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);

  // filtros
  const [filter, setFilter] = useState({
    searchQuery: "",
    searchBy: "document",
    stateFilter: "all",
  });

  // paginación
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;

  // modal editar
  const [showModal, setShowModal] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);

  // ------------------------
  // documentos
  // ------------------------
  const [showDocModal, setShowDocModal] = useState(false);
  const [selectedContractDocs, setSelectedContractDocs] = useState(null);
  const [docList, setDocList] = useState([]);
  const [newDocType, setNewDocType] = useState("");
  const [newDocFile, setNewDocFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [docError, setDocError] = useState("");

  // ALERTAS EDITAR
  const [formAlert, setFormAlert] = useState("");


  // =======================
  //     Cargar contratos
  // =======================
  const loadContracts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(ENDPOINTS.getContract);

      if (res.success && Array.isArray(res.contracts)) {
        setContracts(res.contracts);
      } else {
        setContracts([]);
      }
    } catch (err) {
      console.error("❌ Error cargando contratos:", err);
      setContracts([]);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    if (!token) return;
    loadContracts();
  }, [token, loadContracts]);

  // =======================
  //     Manejadores filtros
  // =======================
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter((f) => ({ ...f, [name]: value }));
    setCurrentPage(1);
  };

  const handleSearchInput = (e) => {
    setFilter((f) => ({ ...f, searchQuery: e.target.value }));
    setCurrentPage(1);
  };

  // =======================
  //     Filtrar resultados
  // =======================
  const filterResults = () => {
    const q = filter.searchQuery.trim().toLowerCase();

    return contracts.filter((c) => {
      if (filter.stateFilter !== "all") {
        if (String(c.state) !== filter.stateFilter) return false;
      }

      if (!q) return true;

      switch (filter.searchBy) {
        case "job_position":
          return String(c.job_position ?? "").toLowerCase().includes(q);

        case "program_name":
          return String(c.program_name ?? "").toLowerCase().includes(q);

        default:
          return String(c.document ?? "").toLowerCase().includes(q);
      }
    });
  };

  const filtered = filterResults();

  // paginación
  const totalPages = Math.max(1, Math.ceil(filtered.length / recordsPerPage));
  const indexOfLast = currentPage * recordsPerPage;
  const indexOfFirst = indexOfLast - recordsPerPage;
  const currentContracts = filtered.slice(indexOfFirst, indexOfLast);

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  // =======================
  //     Abrir Modal (editar)
  // =======================
  const handleOpenModal = (contract) => {
    const formatDate = (datetime) => {
      if (!datetime) return "";

      if (datetime.includes("T")) return datetime.split("T")[0];
      if (datetime.includes(" ")) return datetime.split(" ")[0];

      return datetime;
    };

    setSelectedContract({
      ...contract,
      start_date: formatDate(contract.start_date),
      end_date: formatDate(contract.end_date),
    });

    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedContract(null);
  };

  // =======================
  //     Modificar inputs (editar)
  // =======================
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSelectedContract((prev) => ({ ...prev, [name]: value }));
  };

  // =======================
  //     Guardar cambios (editar)
  // =======================
const handleSaveChanges = async () => {
  if (!selectedContract) return;

  // limpiar alerta previa
  setFormAlert("");

  const { end_date, final_observation, state, id_contrato } = selectedContract;

  // VALIDACIÓN FRONT
  if (!id_contrato || !end_date || !final_observation || !state) {
    setFormAlert("⚠️ Debes completar todos los campos obligatorios antes de guardar.");
    return;
  }

  try {
    const res = await authFetch(ENDPOINTS.updateContract, {
      method: "PUT",
      body: JSON.stringify(selectedContract),
    });

    if (res.success) {
      loadContracts();
      setShowModal(false);
    } else {
      // mensaje controlado desde backend
      setFormAlert(res.message || "No se pudo actualizar el contrato.");
    }
  } catch (error) {
    console.error("❌ Error actualizando:", error);
    setFormAlert("Error de conexión con el servidor.");
  }
};


  // =======================
  //   DOCUMENTOS: abrir modal
  // =======================
  const handleOpenDocuments = async (contract) => {
    setSelectedContractDocs(contract);
    setShowDocModal(true);
    setDocList([]);
    setDocError("");
    setNewDocType("");
    setNewDocFile(null);

    try {
      const url = `${ENDPOINTS.getDocuments}?id_contrato=${contract.id_contrato}`;

      const res = await authFetch(url, { method: "GET" });

      const documents = res?.documents ?? res?.docs ?? [];

      setDocList(Array.isArray(documents) ? documents : []);
    } catch (err) {
      console.error("Error cargando documentos:", err);
      setDocError("No se pudieron cargar los documentos");
    }
  };

  const handleCloseDocModal = () => {
    setShowDocModal(false);
    setSelectedContractDocs(null);
    setDocList([]);
    setNewDocType("");
    setNewDocFile(null);
    setDocError("");
    setUploading(false);
  };

  // =======================
  //   DOCUMENTOS: subir archivo
  // =======================
  const handleUploadDocument = async () => {
    setDocError("");
    if (!selectedContractDocs) {
      setDocError("No hay contrato seleccionado");
      return;
    }
    if (!newDocType) {
      setDocError("Seleccione el tipo de documento");
      return;
    }
    if (!newDocFile) {
      setDocError("Seleccione un archivo");
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append("id_contrato", selectedContractDocs.id_contrato);
      formData.append("tipo_documento", newDocType);
      formData.append("archivo", newDocFile);

      const response = await fetch(ENDPOINTS.uploadDocument, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (data.success) {
        await handleOpenDocuments(selectedContractDocs);
        setNewDocType("");
        setNewDocFile(null);
      } else {
        setDocError(data.message || "Error subiendo documento");
      }
    } catch (err) {
      console.error("Error subiendo documento:", err);
      setDocError("Error subiendo documento");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (doc) => {
    if (!window.confirm(`¿Seguro que deseas eliminar el archivo "${doc.name}"?`))
      return;

    try {
      const res = await authFetch(ENDPOINTS.deleteDocument, {
        method: "DELETE",
        body: JSON.stringify({
          id_contrato: selectedContractDocs.id_contrato,
          filename: doc.name,
        }),
      });

      if (res.success) {
        await handleOpenDocuments(selectedContractDocs);
      } else {
        alert("No se pudo eliminar el documento.");
      }
    } catch (err) {
      console.error("Error eliminando documento:", err);
    }
  };

  const getDocumentUrl = (doc) => {
    if (!doc) return "#";
    if (doc.url) {
      return `${API_BASE_DOCS}${doc.url}`;
    }
    return "#";
  };

  if (loading) return <p>Cargando contratos...</p>;

  return (
    <div className="container mt-4 p-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Contratos gestión humana</h2>

        <button
          className="btn btn-outline-pink"
            onClick={() => navigate("/session/contract_gh/minutas")}
        >
           <i className="bi bi-bookmark-fill me-2"></i>Minutas
        </button>
        <button
          className="btn btn-outline-pink"
            onClick={() => navigate("/session/contract_gh/otrosi")}
        >
           <i className="bi bi-folder-plus me-2"></i>Otro Si
        </button>
        <button
          className="btn btn-outline-pink"
          onClick={() => navigate("/session/contract_gh/certificate")}
        >
           <i className="bi bi-journal-arrow-down me-2"></i>Certificado laboral
        </button>
        
        <button
          className="btn btn-outline-pink"
          onClick={() => navigate("/session/contract_gh/createContract")}
        >
          <i className="bi bi-plus-lg me-1"></i> Nuevo contrato
        </button>
      </div>

      {/* FILTROS */}
      <div className="row mb-3">
        <div className="col-md-6">
          <label className="m-1">Buscar</label>
          <input
            type="text"
            className="form-control"
            placeholder="Buscar por documento, cargo o programa..."
            value={filter.searchQuery}
            onChange={handleSearchInput}
          />
        </div>

        <div className="col-md-3">
          <label className="m-1">Buscar por:</label>
          <select
            className="form-select"
            name="searchBy"
            value={filter.searchBy}
            onChange={handleFilterChange}
          >
            <option value="document">Documento</option>
            <option value="job_position">Cargo</option>
            <option value="program_name">Programa</option>
          </select>
        </div>

        <div className="col-md-3">
          <label className="m-1">Estado:</label>
          <select
            className="form-select"
            name="stateFilter"
            value={filter.stateFilter}
            onChange={handleFilterChange}
          >
            <option value="all">Todos</option>
            <option value="1">Vigente</option>
            <option value="2">Finalizado</option>
          </select>
        </div>
      </div>

      {/* TABLA */}
      {currentContracts.length > 0 ? (
        <>
          <table className="table table-hover">
            <thead className="table-secondary">
              <tr>
                <th>ID</th>
                <th>Documento</th>
                <th>Contratista</th>
                <th>Cargo</th>
                <th>Tipo contrato</th>
                <th>Área</th>
                <th>Programa</th>
                <th>P.prueba hasta</th>
                <th>Estado</th>
                <th style={{ width: "220px" }}>Acciones</th>
              </tr>
            </thead>

            <tbody>
              {currentContracts.map((c) => (
                <tr key={c.id_contrato}>
                  <td>{c.id_contrato}</td>
                  <td>{c.document}</td>
                  <td>
                    {c.first_name} {c.last_name} {c.second_last_name}
                  </td>
                  <td>{c.job_position ?? "-"}</td>
                  <td>{c.type_contract ?? "-"}</td>
                  <td>{c.area_name ?? "-"}</td>
                  <td>{c.program_name ?? "-"}</td>
                  <td>
                    {c.periodo_prueba && c.periodo_prueba !== "No aplica"
                      ? new Date(c.periodo_prueba).toLocaleDateString("es-CO")
                      : "No aplica"}
                  </td>
                 <td>{Number(c.state) === 1 ? "Vigente" : "Finalizado"}</td>
                  <td>
                    <button
                      className="btn btn-sm btn-secondary me-2"
                      onClick={() => handleOpenModal(c)}
                    >
                      Modificar
                    </button>

                    <button
                      className="btn btn-sm btn-outline-purple"
                      onClick={() => handleOpenDocuments(c)}
                    >
                      <i className="bi bi-folder2-open me-1"></i> Documentos
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* ⭐ PAGINACIÓN NUEVA ⭐ */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={goToPage}
          />
        </>
      ) : (
        <p>No hay contratos registrados.</p>
      )}

      {/* =======================
          MODAL DE EDICIÓN
      ======================= */}
      {showModal && selectedContract && (
        <div className="modal fade show d-block" tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Modificar contrato</h5>
                <button className="btn-close" onClick={handleCloseModal}></button>
              </div>

              <div className="modal-body">
                {formAlert && (
                  <div className="alert alert-warning">
                    {formAlert}
                  </div>
                )}

                {/* Fila 1 - Colaborador */}
                <div className="mb-3">
                  <label className="form-label">Colaborador</label>
                  <input
                    type="text"
                    className="form-control"
                    value={`${selectedContract.first_name ?? ""
                      } ${selectedContract.last_name ?? ""}`}
                    readOnly
                    disabled
                  />
                </div>


                {/* Fechas */}
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Fecha Fin</label>
                    <input
                      type="date"
                      className="form-control"
                      name="end_date"
                      value={selectedContract.end_date || ""}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                {/* Observación */}
                <div className="mb-3 col-md-12">
                  <label className="form-label">Observación final</label>
                  <textarea
                    className="form-control"
                    name="final_observation"
                    rows="3"
                    value={selectedContract.final_observation || ""}
                    onChange={handleInputChange}
                  ></textarea>
                </div>

                {/* Estado */}
                <div className="mb-3">
                  <label className="form-label">Estado</label>
                  <select
                    className="form-select"
                    name="state"
                    value={selectedContract.state}
                    onChange={handleInputChange}
                  >
                    <option value="1">Vigente</option>
                    <option value="2">Finalizado</option>
                  </select>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={handleCloseModal}
                >
                  Cancelar
                </button>
                <button
                  className="btn btn-outline-pink"
                  onClick={handleSaveChanges}
                >
                  Guardar cambios
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* =======================
          MODAL DOCUMENTOS
      ======================= */}
      {showDocModal && selectedContractDocs && (
        <div className="modal fade show d-block" tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Documentos - COD{selectedContractDocs.id_contrato}
                </h5>
                <button className="btn-close" onClick={handleCloseDocModal}></button>
              </div>

              <div className="modal-body">
                {/* Lista */}
                <h6>Documentos cargados</h6>
                {docError && (
                  <div className="alert alert-danger">{docError}</div>
                )}

                <ul className="list-group mb-3">
                  {docList.length === 0 && (
                    <li className="list-group-item">Sin documentos</li>
                  )}

                  {docList.map((doc, idx) => (
                    <li
                      key={idx}
                      className="list-group-item d-flex justify-content-between align-items-center"
                    >
                      <div>
                        <strong>{doc.name ?? doc.filename ?? doc}</strong>
                        <div className="small text-muted">
                          {doc.type ? doc.type : ""}
                        </div>
                      </div>

                      <div>
                        <a
                          className="btn btn-sm btn-outline-pink me-2"
                          href={getDocumentUrl(doc)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Ver
                        </a>

                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleDeleteDocument(doc)}
                        >
                          <i className="bi bi-trash3-fill"></i>
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>

                {/* Subir */}
                <h6>Agregar documento</h6>
                <div className="row">
                  <div className="col-md-6 mb-2">
                    <select
                      className="form-select"
                      value={newDocType}
                      onChange={(e) => setNewDocType(e.target.value)}
                    >
                      <option value="" hidden>Seleccione tipo...</option>
                      <option value="EPS">Eps</option>
                      <option value="ARL">Arl</option>
                      <option value="CAJA">Caja</option>
                      <option value="Pension">Pensión</option>
                      <option value="Certificado bancario">Certificado bancario</option>
                      <option value="Contrato">Contrato</option>
                      <option value="Induccion">Inducción</option>
                      <option value="Medidas correctivas">Medidas correctivas</option>
                      <option value="Funciones adicionales">
                        Funciones adicionales
                      </option>
                    </select>
                  </div>

                  <div className="col-md-6 mb-2">
                    <input
                      type="file"
                      className="form-control"
                      onChange={(e) => setNewDocFile(e.target.files[0])}
                    />
                  </div>
                </div>

                <div className="d-flex align-items-center">
                  <button
                    className="btn btn-outline-pink me-2"
                    onClick={handleUploadDocument}
                    disabled={uploading}
                  >
                    {uploading ? "Subiendo..." : "Subir documento"}
                  </button>

                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => {
                      setNewDocType("");
                      setNewDocFile(null);
                      setDocError("");
                    }}
                  >
                    Limpiar
                  </button>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={handleCloseDocModal}
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractGH;
